import { useState } from 'react';
import {
  Button, Dialog, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Tooltip,
} from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

interface FgtsConsult {
  id: string;
  provider: string;
  channel: string | null;
  source: string | null;
  result: string;
  v8Status: string | null;
  amount: number | null;
  errorMessage: string | null;
  cpf: string;
  createdAt: string;
}

/** Cor do chip conforme o GetBalanceResult da consulta. */
function resultColor(result?: string | null): 'success' | 'error' | 'warning' | 'default' {
  const r = String(result || '');
  if (r === 'ok') return 'success';
  if (r === 'pending' || r === 'needAuthBanks' || r === 'waitBirth' || r === 'tryAgain') return 'warning';
  if (r === 'noFounds' || r === 'unknown') return 'default';
  return 'error';
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
}

function fmtMoney(v?: number | null): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Botão (para o menu do chat) que abre o histórico de consultas de saldo FGTS
 * do chat/CPF (fgts_consult_log), com provider, canal, resultado e valor.
 */
export function FgtsConsultHistory({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consults, setConsults] = useState<FgtsConsult[] | undefined>(undefined);

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Histórico de consultas FGTS</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Histórico de consultas FGTS</DialogTitle>
        <DialogContent>
          {loading ? <CircularLoading /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!consults || consults.length === 0) && (
                  <TableRow><TableCell colSpan={5} align="center" style={{ color: '#00000080' }}>Nenhuma consulta.</TableCell></TableRow>
                )}
                {(consults ?? []).map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(c.createdAt)}</TableCell>
                    <TableCell>{c.provider}</TableCell>
                    <TableCell>{c.channel ?? c.source ?? '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={c.errorMessage ?? ''}>
                        <Chip size="small" variant="outlined" color={resultColor(c.result)} label={c.result ?? '—'} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>{fmtMoney(c.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (consults) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.fgtsConsults(chatId)).then((e) => {
      setConsults(e.data?.data?.consults ?? []);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      openGlobalSnack('Não foi possível carregar o histórico de consultas FGTS.', 'error');
    });
  }
}
